import { useState, useRef, DragEvent } from 'react';
import { generateThumbnail, getVideoMetadata } from '../../utils/thumbnailGenerator';
import { formatTime } from '../../utils/formatters';
import type { VideoClip } from '../../types';
import { Button } from '../ui';

export interface VideoUploaderProps {
    onClipsChange: (clips: VideoClip[]) => void;
    onClipSelect?: (clip: VideoClip) => void;
    maxFiles?: number;
}

export function VideoUploader({
    onClipsChange,
    onClipSelect,
    maxFiles = 10
}: VideoUploaderProps) {
    const [clips, setClips] = useState<VideoClip[]>([]);
    const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelection = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const videoFiles = Array.from(files).filter(file =>
            file.type.startsWith('video/')
        );

        if (videoFiles.length === 0) {
            alert('Vælg venligst gyldige videofiler');
            return;
        }

        if (clips.length + videoFiles.length > maxFiles) {
            alert(`Du kan maksimalt uploade ${maxFiles} videoer`);
            return;
        }

        // Process each file
        const newClips: VideoClip[] = [];
        for (const file of videoFiles) {
            try {
                const thumbnail = await generateThumbnail(file);
                const metadata = await getVideoMetadata(file);

                const clip: VideoClip = {
                    id: `${Date.now()}-${Math.random()}`,
                    file,
                    name: file.name,
                    thumbnail,
                    duration: metadata.duration,
                    startTime: 0,
                    endTime: metadata.duration,
                    metadata,
                };

                newClips.push(clip);
            } catch (error) {
                console.error(`Error processing ${file.name}:`, error);
                alert(`Fejl ved tilføjelse af ${file.name}`);
            }
        }

        const updatedClips = [...clips, ...newClips];
        setClips(updatedClips);
        onClipsChange(updatedClips);
    };

    const handleAddClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        handleFileSelection(files);
    };

    const handleClipClick = (clip: VideoClip) => {
        setSelectedClipId(clip.id);
        onClipSelect?.(clip);
    };

    const handleDeleteClip = (clipId: string, e: React.MouseEvent) => {
        e.stopPropagation();

        if (!confirm('Er du sikker på at du vil slette dette klip?')) {
            return;
        }

        const updatedClips = clips.filter(c => c.id !== clipId);
        setClips(updatedClips);
        onClipsChange(updatedClips);

        if (selectedClipId === clipId) {
            setSelectedClipId(null);
        }
    };

    return (
        <aside className="w-80 bg-gray-800/50 backdrop-blur-lg border-r border-gray-700 flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-700">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold">Uploadede Klip</h2>
                    <span className="px-2 py-1 bg-primary-600/20 text-primary-400 text-sm font-medium rounded">
                        {clips.length}
                    </span>
                </div>
            </div>

            {/* Clip List */}
            <div
                className={`flex-1 overflow-y-auto scrollbar-thin p-4 ${isDragging ? 'bg-primary-600/10' : ''
                    }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {clips.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                        <svg className="w-16 h-16 text-gray-600 mb-4" fill="none" viewBox="0 0 64 64">
                            <path d="M32 8V56M8 32H56" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                        </svg>
                        <p className="text-gray-400 mb-2">Ingen klip uploadet endnu</p>
                        <p className="text-sm text-gray-500">Upload videoklip for at komme i gang</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {clips.map(clip => (
                            <div
                                key={clip.id}
                                className={`
                  group relative bg-gray-700/50 rounded-lg overflow-hidden cursor-pointer
                  transition-all duration-200 hover:bg-gray-700
                  ${selectedClipId === clip.id ? 'ring-2 ring-primary-500' : ''}
                `}
                                onClick={() => handleClipClick(clip)}
                            >
                                {/* Thumbnail */}
                                <div className="relative aspect-video">
                                    <img
                                        src={clip.thumbnail}
                                        alt={clip.name}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                                    {/* Duration Badge */}
                                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded text-xs font-medium">
                                        {formatTime(clip.endTime - clip.startTime)}
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="p-3">
                                    <p className="text-sm font-medium truncate mb-1">{clip.name}</p>
                                    <p className="text-xs text-gray-400">
                                        {clip.metadata.width}x{clip.metadata.height}
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={(e) => handleDeleteClip(clip.id, e)}
                                        className="p-1.5 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                                        title="Slet"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
                                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Button */}
            <div className="p-4 border-t border-gray-700">
                <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleAddClick}
                    icon={
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
                            <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    }
                >
                    Tilføj Klip
                </Button>
            </div>

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileSelection(e.target.files)}
            />
        </aside>
    );
}
